import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { buildLegGroups, legKeyOf } from '../legGroups'
import { useQuoteI18n } from '../quoteI18n'

// Stessa convenzione di Services/FeathersClient.js: l'URL dell'API viene iniettato a
// runtime in `window._env_` da deploy/loadEnv.sh. `import.meta.env` non è utilizzabile
// qui perché richiederebbe la variabile al build time (in dev resta vuota e la fetch
// finisce sul dev server Vite, che risponde con index.html).
const API_BASE_URL = window._env_?.API_BASE_URL || 'http://localhost:3000'

/**
 * Ogni quanto ricontrollare lo stato del preventivo mentre la prenotazione è in corso.
 *
 * Il booking è asincrono (Executor) e può chiedere altri dati: il preventivo passa a
 * `awaitingData` con il form da mostrare. La pagina non ha un canale push, quindi
 * ricontrolla — 3 secondi è abbastanza reattivo senza pesare sull'API.
 */
const POLL_INTERVAL_MS = 3000

/** Per quanto tempo continuare a controllare prima di dichiarare l'attesa troppo lunga. */
const POLL_TIMEOUT_MS = 2 * 60 * 1000

/**
 * Il token di sblocco vive nel `sessionStorage`, non nello stato React: così un refresh
 * non richiede di reinserire il PIN, ma chiudere la scheda sì. È legato al token del
 * preventivo, perché due preventivi diversi hanno sblocchi diversi.
 */
const unlockKey = (token) => `goelettQuoteUnlock:${token}`

/**
 * Le fasi della prenotazione, in ordine di avanzamento.
 *
 * L'ordine **è** la semantica: la fase mostrata è sempre la più avanzata fra quella che
 * dice il server e quella che sappiamo noi per aver appena inviato qualcosa. Serve per due
 * motivi opposti:
 *
 * - subito dopo un invio il server è ancora indietro (l'Executor lavora in asincrono) e
 *   senza il minimo locale la pagina tornerebbe indietro di una schermata;
 * - dopo un **refresh** il minimo locale non c'è più e comanda il server, che è l'unico a
 *   sapere davvero a che punto siamo. Prima il refresh riportava alla scelta delle offerte
 *   anche a prenotazione già inviata.
 */
const PHASE_ORDER = ['choosing', 'preparing', 'form', 'finalizing', 'done']

const rank = (phase) => {
  const index = PHASE_ORDER.indexOf(phase)
  return index === -1 ? 0 : index
}

/**
 * A che punto è la prenotazione secondo il record del preventivo.
 *
 * `bookingData` è la discriminante fra le due attese che condividono lo stato `booking`:
 * senza, il trip si sta ancora creando; con, il form è già stato compilato e si sta
 * concludendo. Sono due messaggi diversi per il viaggiatore, e dopo un refresh questa è
 * l'unica informazione disponibile per distinguerli.
 */
const serverPhase = (data) => {
  switch (data?.status) {
    case 'confirmed':
      return 'done'
    case 'awaitingData':
      return 'form'
    case 'booking':
      return data?.bookingData ? 'finalizing' : 'preparing'
    default:
      return 'choosing'
  }
}

/**
 * Hook della pagina preventivo: sblocco con PIN, selezione dei servizi, prenotazione,
 * form dei dati richiesti ed esito.
 *
 * Gli errori mostrati al viaggiatore non sono il testo dell'API: l'API risponde con un
 * `errorCode` stabile e la pagina lo traduce nella lingua corrente. Il testo del server
 * resta come fallback per un codice che non conosciamo (o per un'API non ancora
 * aggiornata), perché è comunque meglio di un messaggio vuoto.
 *
 * @param {string} token - Token del preventivo
 */
export function useGoelettQuote(token) {
  const { t } = useQuoteI18n()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  /** Fase minima garantita da un'azione appena compiuta su questa scheda. */
  const [localPhase, setLocalPhase] = useState('choosing')
  /** True quando l'attesa ha superato il timeout senza che il server si muovesse. */
  const [stalled, setStalled] = useState(false)
  /** Offerte scelte, indicizzate per tratta (`legKey`): una sola per tratta. */
  const [selectionsByLeg, setSelectionsByLeg] = useState({})
  const [unlockToken, setUnlockToken] = useState(
    () => sessionStorage.getItem(unlockKey(token)) || ''
  )
  const [verifyingPin, setVerifyingPin] = useState(false)
  const [pinError, setPinError] = useState(null)
  /** Millisecondi rimasti per prenotare, `null` quando il termine non si applica. */
  const [msLeft, setMsLeft] = useState(null)
  /**
   * Scarto fra l'orologio del client e quello del server, misurato a ogni lettura.
   *
   * `expiresAt` è un istante assoluto: un client con l'ora sbagliata di dieci minuti
   * mostrerebbe scaduto un preventivo valido, o il contrario. Misurando lo scarto una volta
   * il conto alla rovescia usa il *tempo trascorso* locale, che è affidabile, e non l'ora
   * locale, che non lo è.
   */
  const clockOffset = useRef(0)

  const authHeaders = useCallback(
    () => (unlockToken ? { 'x-quote-unlock': unlockToken } : {}),
    [unlockToken]
  )

  /**
   * Messaggio da mostrare per una risposta di errore dell'API.
   *
   * @param {Object} body - Corpo JSON della risposta (può essere vuoto)
   * @param {number} status - Status HTTP
   * @returns {string} Testo tradotto
   */
  const errorMessage = useCallback(
    (body, status) =>
      t.errors[body?.errorCode] || body?.error || body?.message || t.errors.generic(status),
    [t]
  )

  /**
   * Applica alla pagina un preventivo appena letto.
   *
   * Il testo dell'errore si imposta qui e non solo al primo caricamento: la prenotazione può
   * fallire **mentre** la pagina è in polling, e in quel caso il pannello mostrerebbe il
   * testo generico invece di dire cosa è successo davvero (offerta non più disponibile,
   * prezzo cambiato, prenotazione parziale).
   *
   * @param {Object} result - Corpo della risposta del preventivo
   */
  const applyQuote = useCallback(
    (result) => {
      const serverNow = Date.parse(result?.serverTime)
      if (Number.isFinite(serverNow)) clockOffset.current = serverNow - Date.now()
      if (result?.unlockToken) {
        sessionStorage.setItem(unlockKey(token), result.unlockToken)
        setUnlockToken(result.unlockToken)
      }
      setData(result)
      if (result?.expired) setError(errorMessage(result, 410))
      else if (result?.status === 'error') setError(errorMessage(result, 409))
    },
    [errorMessage, token]
  )

  const fetchQuote = useCallback(async () => {
    const params = new URLSearchParams()
    const currentUnlock = unlockToken || sessionStorage.getItem(unlockKey(token)) || ''
    if (currentUnlock) params.set('unlockToken', currentUnlock)
    const qs = params.toString()
    const url = `${API_BASE_URL}/goelettQuotes/${token}${qs ? `?${qs}` : ''}`
    const res = await fetch(url, { headers: authHeaders() })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(errorMessage(body, res.status))
    }
    return res.json()
  }, [token, authHeaders, errorMessage, unlockToken])

  useEffect(() => {
    if (!token) return
    let cancelled = false
    setLoading(true)
    setError(null)

    fetchQuote()
      .then((result) => {
        if (cancelled) return
        applyQuote(result)
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false))

    return () => {
      cancelled = true
    }
  }, [token, fetchQuote, applyQuote])

  /**
   * Il termine per prenotare si applica solo mentre il preventivo aspetta il viaggiatore.
   *
   * In `booking` sta lavorando l'Executor: un conto alla rovescia che arriva a zero mentre
   * `BookTrip` è in volo direbbe "scaduto" di una prenotazione che magari è appena riuscita.
   * Stessa regola del servizio, che di quello stato non tocca la scadenza.
   */
  const deadlineApplies = data?.status === 'pending' || data?.status === 'awaitingData'
  const deadlineMs = data?.expiresAt ? Date.parse(data.expiresAt) : null

  useEffect(() => {
    if (!deadlineApplies || !Number.isFinite(deadlineMs)) {
      setMsLeft(null)
      return undefined
    }
    const compute = () => setMsLeft(Math.max(0, deadlineMs - (Date.now() + clockOffset.current)))
    compute()
    const ticker = setInterval(compute, 1000)
    return () => clearInterval(ticker)
  }, [deadlineApplies, deadlineMs])

  const phase = useMemo(() => {
    if (loading) return 'loading'
    if (error && !data) return 'failed'
    if (data?.expired || data?.status === 'expired') return 'expired'
    // Scaduto sul nostro orologio: la pagina lo dice subito invece di far compilare un form
    // o premere "Prosegui" per farsi rifiutare dal server un istante dopo.
    if (deadlineApplies && msLeft === 0) return 'expired'
    if (data?.locked) return 'locked'
    if (data?.status === 'error') return 'failed'
    const fromServer = serverPhase(data)
    return rank(fromServer) >= rank(localPhase) ? fromServer : localPhase
  }, [loading, error, data, localPhase, deadlineApplies, msLeft])

  const isWaiting = phase === 'preparing' || phase === 'finalizing'

  /**
   * Mentre si aspetta l'Executor, si ricontrolla il preventivo finché non cambia fase.
   *
   * Vive in un effetto legato alla fase e non nei gestori dei pulsanti: così riparte da
   * sé anche quando la pagina viene **ricaricata** durante l'attesa, che è esattamente il
   * caso in cui prima restava ferma per sempre.
   */
  const pollTimer = useRef(null)
  useEffect(() => {
    if (!isWaiting) return undefined

    let cancelled = false
    setStalled(false)
    const deadline = Date.now() + POLL_TIMEOUT_MS

    const check = async () => {
      if (cancelled) return
      if (Date.now() > deadline) {
        setStalled(true)
        return
      }
      try {
        const result = await fetchQuote()
        if (cancelled) return
        applyQuote(result)
      } catch {
        // Errore di rete transitorio: si riprova al giro successivo.
      }
      if (!cancelled) pollTimer.current = setTimeout(check, POLL_INTERVAL_MS)
    }

    pollTimer.current = setTimeout(check, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearTimeout(pollTimer.current)
    }
  }, [isWaiting, fetchQuote, applyQuote])

  /**
   * Verifica il PIN ricevuto e sblocca il preventivo.
   * @param {string} pin - Codice di 5 cifre
   */
  const handleUnlock = useCallback(
    async (pin) => {
      if (verifyingPin) return
      setVerifyingPin(true)
      setPinError(null)

      try {
        const res = await fetch(`${API_BASE_URL}/goelettQuotes/${token}/unlock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin })
        })
        const body = await res.json().catch(() => ({}))
        if (!res.ok) {
          setData((current) => ({ ...current, attemptsLeft: body.attemptsLeft ?? 0 }))
          throw new Error(errorMessage(body, res.status))
        }
        sessionStorage.setItem(unlockKey(token), body.unlockToken)
        setUnlockToken(body.unlockToken)
      } catch (err) {
        setPinError(err.message)
      } finally {
        setVerifyingPin(false)
      }
    },
    [token, verifyingPin, errorMessage]
  )

  /**
   * Seleziona o deseleziona un'offerta; una sola per **tratta**.
   *
   * La chiave è la tratta e non il verticale: su un A/R in treno servono due offerte, e
   * chiudere su `segmentType` significava che scegliere il ritorno cancellava l'andata.
   */
  const toggleOffer = useCallback((offer) => {
    const leg = legKeyOf(offer)
    setSelectionsByLeg((current) => {
      const chosen = current[leg]
      if (chosen?.offerId === offer.offerId) {
        const { [leg]: _removed, ...rest } = current
        return rest
      }
      return { ...current, [leg]: offer }
    })
  }, [])

  const selections = useMemo(() => Object.values(selectionsByLeg), [selectionsByLeg])

  const legGroups = useMemo(() => buildLegGroups(data?.offers || {}), [data?.offers])

  const allLegsSelected = useMemo(() => {
    if (!legGroups.length) return false
    return legGroups.every((group) => selectionsByLeg[group.key])
  }, [legGroups, selectionsByLeg])

  /** Avvia la prenotazione di tutti i servizi scelti, come un unico viaggio. */
  const handleBook = useCallback(async () => {
    if (!token || submitting || phase !== 'choosing' || !allLegsSelected) {
      if (!allLegsSelected && selections.length > 0) {
        setError(t.errors.incompleteSelection)
      }
      return
    }
    setSubmitting(true)
    setError(null)

    try {
      const res = await fetch(`${API_BASE_URL}/goelettQuotes/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          selections: selections.map((offer) => ({
            offerId: offer.offerId,
            segmentType: offer.segmentType
          }))
        })
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(errorMessage(body, res.status))
      }

      // L'accettazione non conclude nulla: apre l'attesa in cui l'Executor crea il trip e
      // scopre quali dati servono ancora. Il polling parte da sé al cambio di fase.
      setLocalPhase('preparing')
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }, [token, submitting, phase, allLegsSelected, selections, authHeaders, errorMessage, t])

  /**
   * Invia i dati compilati nel form e riprende la prenotazione.
   * @param {Object} values - Valori indicizzati per chiave di campo
   */
  const handleSubmitBookingData = useCallback(
    async (values) => {
      if (!token || submitting) return
      setSubmitting(true)
      setError(null)

      try {
        const res = await fetch(`${API_BASE_URL}/goelettQuotes/${token}/booking-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ values })
        })

        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(errorMessage(body, res.status))
        }

        setLocalPhase('finalizing')
      } catch (err) {
        setError(err.message)
      } finally {
        setSubmitting(false)
      }
    },
    [token, submitting, authHeaders, errorMessage]
  )

  return {
    data,
    phase,
    error,
    stalled,
    msLeft,
    verifyingPin,
    pinError,
    handleUnlock,
    selectionsByLeg,
    selections,
    legGroups,
    allLegsSelected,
    toggleOffer,
    submitting,
    bookingForm: data?.requiredBookingData?.form || null,
    handleBook,
    handleSubmitBookingData
  }
}
